import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { usePageSEO } from "../lib/seo";
import { ArrowLeft, Send, Loader2, CheckCircle, MapPin, Mail } from "lucide-react";

export default function ContactPage() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  usePageSEO({ title: "Contact Us", description: "Get in touch with Stansbury AI. Offices in Miami, FL and Toronto, ON.", path: "/contact" });

  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captcha, setCaptcha] = useState({ a: 0, b: 0 });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setCaptcha({ a: Math.floor(Math.random() * 9) + 1, b: Math.floor(Math.random() * 9) + 1 });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (parseInt(captchaAnswer) !== captcha.a + captcha.b) {
      setError("Incorrect answer. Please try again.");
      setCaptcha({ a: Math.floor(Math.random() * 9) + 1, b: Math.floor(Math.random() * 9) + 1 });
      setCaptchaAnswer("");
      return;
    }
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setSent(true);
    } catch (err) {
      setError("Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="contact-page">
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16">
          <button onClick={() => navigate("/")}><img src="/stansbury-logo.png" alt="Stansbury AI" className="h-[46px] sm:h-12 flex-shrink-0" /></button>
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-600 font-['Outfit'] text-sm"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">Get In Touch</p>
        <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-2">Contact Us</h1>
        <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-12">Have a question or want to learn more? We'd love to hear from you.</p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Form */}
          <div className="lg:col-span-3">
            {sent ? (
              <div className="bg-white border border-slate-200 p-10 text-center">
                <div className="w-16 h-16 bg-emerald-50 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-2">Message Sent</h2>
                <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-6">Thank you for reaching out. We'll get back to you within one business day.</p>
                <Button onClick={() => navigate("/")} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-8 h-11 font-['Outfit']">Back to Home</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white border border-slate-200 p-8 space-y-5" data-testid="contact-form">
                {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3" data-testid="contact-error">{error}</div>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Full Name *</Label>
                    <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="John Smith" className="mt-1.5 rounded-none border-slate-300" data-testid="contact-name" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Email *</Label>
                    <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="john@company.com" className="mt-1.5 rounded-none border-slate-300" data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Company</Label>
                  <Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Acme Inc." className="mt-1.5 rounded-none border-slate-300" data-testid="contact-company" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Message *</Label>
                  <Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required placeholder="How can we help you?" className="mt-1.5 rounded-none border-slate-300 min-h-[120px]" data-testid="contact-message" />
                </div>
                <div className="bg-slate-50 border border-slate-200 p-4">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Security Check</Label>
                  <div className="flex items-center gap-3">
                    <span className="text-base font-['JetBrains_Mono'] text-[#0F172A]">What is {captcha.a} + {captcha.b}?</span>
                    <Input type="number" value={captchaAnswer} onChange={e => setCaptchaAnswer(e.target.value)} required placeholder="?" className="w-20 rounded-none border-slate-300 text-center font-['JetBrains_Mono']" data-testid="contact-captcha" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-11 font-['Outfit'] font-medium" data-testid="contact-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Send Message</>}
                </Button>
              </form>
            )}
          </div>

          {/* Offices */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-[#0033CC]" />
                <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">United States</h3>
              </div>
              <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">
                Stansbury AI<br />
                2222 Ponce De Leon Blvd<br />
                Miami, FL 33134<br />
                USA
              </p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-[#0033CC]" />
                <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Canada</h3>
              </div>
              <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">
                Stansbury AI<br />
                2967 Dundas St. W. #249D<br />
                Toronto, ON M6P 1Z2<br />
                Canada
              </p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-4 h-4 text-[#0033CC]" />
                <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Email</h3>
              </div>
              <p className="text-sm text-slate-600 font-['IBM_Plex_Sans']">hello@stansbury.ai</p>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-slate-950 text-white py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row justify-between items-center gap-4">
          <img src="/stansbury-logo-white.png" alt="Stansbury AI" className="h-10" />
          <div className="flex items-center gap-4">
            <a href="/privacy" className="text-xs text-slate-500 hover:text-slate-300 font-['IBM_Plex_Sans'] transition-colors">Privacy Policy</a>
            <span className="text-slate-700">|</span>
            <a href="/contact" className="text-xs text-slate-500 hover:text-slate-300 font-['IBM_Plex_Sans'] transition-colors">Contact</a>
            <span className="text-slate-700">|</span>
            <p className="text-xs text-slate-500 font-['IBM_Plex_Sans']">Confidential & Secure</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
